import type { OrchestrationPendingRequest, OrchestrationThreadSummary } from "@t3tools/contracts";

import { isOperatorsForbiddenError } from "./lib/operatorsReactQuery";
import { isOperatorsAuthExpiredError } from "./operatorsAuthSession";
import { isOperatorsSessionParticipant, type OperatorsSession } from "./operators";

export function threadNotificationsPreferenceKey(email: string): string {
  return `operators:thread-notifications:${email}`;
}

export function getThreadNotificationPermission(): NotificationPermission | "unsupported" {
  return typeof window !== "undefined" && window.isSecureContext && "Notification" in window
    ? Notification.permission
    : "unsupported";
}

export function areThreadNotificationsEnabled(email: string): boolean {
  try {
    return (
      getThreadNotificationPermission() === "granted" &&
      localStorage.getItem(threadNotificationsPreferenceKey(email)) !== "false"
    );
  } catch {
    return false;
  }
}

type CompletionThread = Pick<
  OrchestrationThreadSummary,
  "id" | "title" | "deletedAt" | "latestTurn"
>;

// Observe server snapshots rather than assistant messages, which can finish before a turn does.
// The first snapshot is a baseline so opening Operators never announces historical work.
export function createThreadCompletionTracker() {
  let previous: Map<string, CompletionThread["latestTurn"]> | null = null;
  let baselineTime = 0;
  return (threads: readonly CompletionThread[], updatedAt: string): CompletionThread[] => {
    if (!previous) baselineTime = Date.parse(updatedAt);
    const completed: CompletionThread[] = [];
    for (const thread of threads) {
      const turn = thread.latestTurn;
      const priorTurn = previous?.get(thread.id);
      if (
        previous !== null &&
        (previous.has(thread.id) || Date.parse(turn?.requestedAt ?? "") > baselineTime) &&
        !thread.deletedAt &&
        turn?.state === "completed" &&
        turn.completedAt &&
        (priorTurn?.turnId !== turn.turnId ||
          priorTurn.state !== "completed" ||
          !priorTurn.completedAt)
      ) {
        completed.push(thread);
      }
    }
    previous = new Map(threads.map((thread) => [thread.id, thread.latestTurn]));
    return completed;
  };
}

type AttentionThread = CompletionThread & Pick<OrchestrationThreadSummary, "pendingRequests">;

function attentionKey(thread: CompletionThread, request: OrchestrationPendingRequest): string {
  return JSON.stringify([thread.id, thread.latestTurn?.turnId, request.kind, request.requestId]);
}

// Pending requests are actionable even on the first connection. Replayed snapshots
// are deduplicated, and the current set also cancels delivery after a response.
export function createThreadAttentionTracker() {
  let pending = new Set<string>();
  return {
    update(threads: readonly AttentionThread[]) {
      const next = new Set<string>();
      const candidates: Array<{ thread: AttentionThread; request: OrchestrationPendingRequest }> =
        [];
      for (const thread of threads) {
        if (thread.deletedAt || thread.latestTurn?.state !== "running") continue;
        for (const request of thread.pendingRequests ?? []) {
          const key = attentionKey(thread, request);
          next.add(key);
          if (!pending.has(key)) candidates.push({ thread, request });
        }
      }
      pending = next;
      return candidates;
    },
    isPending: (thread: CompletionThread, request: OrchestrationPendingRequest) =>
      pending.has(attentionKey(thread, request)),
  };
}

type NotificationInput = {
  email: string;
  thread: CompletionThread;
  isActive: () => boolean;
  canDeliver?: () => boolean;
  isViewingThread: () => boolean;
  loadSession: () => Promise<OperatorsSession | undefined>;
  openThread: () => void;
  request?: OrchestrationPendingRequest;
  canShowInApp?: () => boolean;
  showInApp?: () => void;
};

export async function notifyThread(input: NotificationInput): Promise<"retry" | void> {
  const { email, thread } = input;
  const turnId = thread.latestTurn?.turnId;
  if (
    !turnId ||
    !input.isActive() ||
    input.canDeliver?.() === false ||
    (!areThreadNotificationsEnabled(email) && !input.showInApp)
  )
    return;

  try {
    let session: OperatorsSession | undefined;
    try {
      session = await input.loadSession();
    } catch (error) {
      if (isOperatorsForbiddenError(error) || isOperatorsAuthExpiredError(error)) return;
      return "retry";
    }
    if (
      !session ||
      session.id !== thread.id ||
      session.archivedAt ||
      !isOperatorsSessionParticipant(session, email)
    )
      return;

    const deliver = () => {
      if (
        !input.isActive() ||
        input.canDeliver?.() === false ||
        (!areThreadNotificationsEnabled(email) && !input.showInApp)
      )
        return;
      const storageKey = `${threadNotificationsPreferenceKey(email)}:delivered`;
      const notificationId = input.request
        ? attentionKey(thread, input.request)
        : JSON.stringify([thread.id, turnId]);
      let delivered: string[] = [];
      try {
        const stored: unknown = JSON.parse(localStorage.getItem(storageKey) ?? "[]");
        if (Array.isArray(stored)) delivered = stored.filter((id) => typeof id === "string");
      } catch {
        // A blocked or corrupt browser store must not break orchestration sync.
      }
      if (delivered.includes(notificationId)) return;

      if (!input.isViewingThread()) {
        const desktopEnabled = areThreadNotificationsEnabled(email);
        const showInApp = input.showInApp && input.canShowInApp?.() !== false;
        if (!desktopEnabled && !showInApp) return "retry" as const;
        if (showInApp) input.showInApp?.();
        if (desktopEnabled) {
          const notification = new Notification(
            input.request
              ? input.request.kind === "approval"
                ? "Agent needs approval"
                : "Agent needs your input"
              : "Agent finished working",
            {
              body: thread.title,
              tag: `operators:${notificationId}`,
            },
          );
          notification.addEventListener("click", () => {
            notification.close();
            if (!input.isActive()) return;
            window.focus();
            input.openThread();
          });
        }
      }
      try {
        localStorage.setItem(
          storageKey,
          JSON.stringify([...delivered, notificationId].slice(-200)),
        );
      } catch {
        // The notification tag still replaces duplicates when storage is unavailable.
      }
    };

    // Serialize the shared deduplication check across browser tabs when supported.
    if (navigator.locks) {
      return await navigator.locks.request(`operators:thread-notifications:${email}`, deliver);
    } else {
      return deliver();
    }
  } catch {
    // Notifications are best effort. Permission changes, lookup failures, or unsupported
    // constructors must never interfere with receiving the next thread update.
  }
}

// Retain candidates separately from the snapshot tracker until delivery has settled.
// Retry for at most five minutes, and release timers and inputs when the user signs out.
export function createThreadNotificationQueue() {
  type Pending = {
    input: NotificationInput;
    expiresAt: number;
    delay: number;
    timer: ReturnType<typeof setTimeout> | null;
    running: boolean;
  };
  const pending = new Map<string, Pending>();
  let disposed = false;
  const attempt = async (key: string, entry: Pending) => {
    if (disposed || entry.running || pending.get(key) !== entry) return;
    if (entry.timer !== null) {
      clearTimeout(entry.timer);
      entry.timer = null;
    }
    entry.running = true;
    const result = await notifyThread({
      ...entry.input,
      isActive: () => !disposed && entry.input.isActive(),
      canDeliver: () =>
        pending.get(key) === entry &&
        Date.now() < entry.expiresAt &&
        entry.input.canDeliver?.() !== false,
    });
    entry.running = false;
    if (disposed || pending.get(key) !== entry) return;
    if (result !== "retry" || Date.now() >= entry.expiresAt) {
      pending.delete(key);
      return;
    }
    entry.timer = setTimeout(() => void attempt(key, entry), entry.delay);
    entry.delay = Math.min(entry.delay * 2, 30_000);
  };
  return {
    enqueue: (input: NotificationInput) => {
      if (disposed) return;
      const key = input.request
        ? attentionKey(input.thread, input.request)
        : JSON.stringify([input.thread.id, input.thread.latestTurn?.turnId]);
      if (pending.has(key)) return;
      if (pending.size >= 200) {
        const oldest = pending.entries().next().value;
        if (oldest) {
          if (oldest[1].timer !== null) clearTimeout(oldest[1].timer);
          pending.delete(oldest[0]);
        }
      }
      const entry: Pending = {
        input,
        expiresAt: Date.now() + 300_000,
        delay: 1_000,
        timer: null,
        running: false,
      };
      pending.set(key, entry);
      void attempt(key, entry);
    },
    retryPending: () => {
      for (const [key, entry] of pending) void attempt(key, entry);
    },
    dispose: () => {
      disposed = true;
      for (const entry of pending.values()) if (entry.timer !== null) clearTimeout(entry.timer);
      pending.clear();
    },
  };
}
